import { useAppContext } from 'hooks/useAppContext';
import { useDeviceSize } from 'hooks/useDeviceSize';
import { useDispatchToParent } from 'hooks/useDispatchToParent';
import { useGetTruckerQueriesData } from 'hooks/useGetTruckerQueriesData';
import React, { useEffect, useState } from 'react';
import { isApprovalValidGR } from 'utils/isApprovalValid';
import { useTruckerProps } from 'hooks/useTruckerProps';
import { useUserHasChat } from 'hooks/useUserHasChat';
import Menu from '../Menu';
import { Avatar } from './Avatar';
import { BackgroundFixed } from './BackgroundFixed';
import { Name } from './Name';
import { RatingAndTrips } from './RatingAndTrips';
import { StickyName } from './StickyName';
import { Tags } from './Tags';
import { TopActionButtons } from './TopActionButtons';
import * as Styled from './styled';
import { ProfileType } from './types';

export const ProfileHeader = (props: ProfileType) => {
  const {
    state: {
      profileSize: { isProfileSizeSmall },
    },
  } = useAppContext();

  const { onClose, isPaidFee, wrapperRef, hasDrawer } = props;

  const { isMobile, isTablet } = useDeviceSize();
  const { truckerData, contactListFeatureData } = useGetTruckerQueriesData();
  const { dispatchToParent } = useDispatchToParent();
  const { hasChatFeature } = useUserHasChat();
  const { freightId } = useTruckerProps();

  const [scrolledValue, setScrolledValue] = useState(0);

  const name = truckerData?.trucker?.name;
  const avatarPic = truckerData?.trucker?.selfieUri;
  const rate = truckerData?.evaluation?.grade;
  const isVerified = truckerData?.isVerified;
  const isVip = truckerData?.trucker?.isVip;
  const ammountTravelled = truckerData?.journey?.total;
  const showCloseButton = hasDrawer || isMobile || isTablet;
  const hasContactFeature = contactListFeatureData?.access;
  const inContactList = truckerData?.trucker?.inContactList;
  const isSmallProfile = isProfileSizeSmall;
  const hasSomeApproval = truckerData?.lastSearches?.totalApprovedSearches > 0;
  const planType = truckerData?.trucker?.planType;
  const isDeposit = planType === 'deposit';
  const showChatButton = !isDeposit && hasChatFeature;
  const truckerUuid = truckerData?.trucker?.uuid;

  const isApprovalValid = isApprovalValidGR({
    hasSomeApproval,
    lastApprovedDate: truckerData?.lastSearches?.lastApprovedDate,
  });

  const showStickyName = (!isSmallProfile && scrolledValue >= 106) || (isSmallProfile && scrolledValue >= 170);

  const handleOnClose = () => {
    dispatchToParent?.({
      type: 'truckers_driver_active_set',
      payload: { activeDriver: null },
    });
    onClose?.();
  };

  useEffect(() => {
    const wrapperRefCurrent = wrapperRef?.current;

    const handleScroll = () => {
      /* istanbul ignore else */
      if (wrapperRefCurrent) {
        setScrolledValue(wrapperRefCurrent.scrollTop);
      }
    };

    /* istanbul ignore else */
    if (wrapperRefCurrent) {
      wrapperRefCurrent.addEventListener('scroll', handleScroll);
    }

    return () => {
      /* istanbul ignore else */
      if (wrapperRefCurrent) {
        wrapperRefCurrent.removeEventListener('scroll', handleScroll);
      }
    };
  }, [wrapperRef]);

  return (
    <>
      <BackgroundFixed scrolledValue={scrolledValue} isSmallProfile={isSmallProfile} />
      <TopActionButtons
        onClose={handleOnClose}
        hasContactFeature={hasContactFeature}
        showCloseButton={showCloseButton}
        isSmallProfile={isSmallProfile}
        scrolledValue={scrolledValue}
      />
      <StickyName
        isVerified={isVerified}
        name={name}
        showCloseButton={showCloseButton}
        isSmallProfile={isSmallProfile}
        isVip={isVip}
        showStickyName={showStickyName}
      />
      <Styled.ContentWrapper isSmallProfile={isSmallProfile}>
        <Avatar
          name={name}
          picture={avatarPic}
          scrolledValue={scrolledValue}
          isSmallProfile={isSmallProfile}
          isPaidFee={isPaidFee}
        />
        <Styled.InfoWrapper
          scrolledValue={scrolledValue}
          showCloseButton={showCloseButton}
          isSmallProfile={isSmallProfile}
        >
          <Name
            inContactlist={inContactList}
            isVerified={isVerified}
            name={name}
            scrolledValue={scrolledValue}
            hasContactFeature={hasContactFeature}
            showCloseButton={showCloseButton}
            isSmallProfile={isSmallProfile}
            isVip={isVip}
            showStickyName={showStickyName}
          />
          <RatingAndTrips
            rate={rate}
            ammountTravelled={ammountTravelled}
            scrolledValue={scrolledValue}
            isSmallProfile={isSmallProfile}
          />
          <Tags scrolledValue={scrolledValue} isSmallProfile={isSmallProfile} isApprovalValid={isApprovalValid} />
          <Styled.MenuWrapper scrolledValue={scrolledValue} isSmallProfile={isSmallProfile} data-testid="menu-wrapper">
            <Menu
              isPaidFee={isPaidFee}
              showChatButton={showChatButton}
              truckerUuid={truckerUuid}
              truckerName={name}
              freightId={freightId}
            />
          </Styled.MenuWrapper>
        </Styled.InfoWrapper>
      </Styled.ContentWrapper>
    </>
  );
};
